import React, { useState, useCallback, useEffect } from 'react';
import { RotateCcw, CheckCircle, AlertCircle } from 'lucide-react';

interface PatternItem {
  id: string;
  emoji: string;
  color: string;
  type: 'shape' | 'color' | 'size';
}

interface PatternMakerResource {
  name: string;
  question: string;
  pattern: PatternItem[];
  options: PatternItem[];
  allowInteraction: boolean;
  showHint: boolean;
  maxLength: number;
}

interface PatternMakerComponentProps {
  resource: PatternMakerResource;
  onAction?: (action: string, data?: any) => void;
}

const PatternMakerComponent: React.FC<PatternMakerComponentProps> = ({ resource, onAction }) => {
  const [currentPattern, setCurrentPattern] = useState<PatternItem[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [completedPatterns, setCompletedPatterns] = useState<number>(0);
  const [totalAttempts, setTotalAttempts] = useState<number>(0);

  const { pattern, options, allowInteraction, showHint, maxLength } = resource;

  // Show feedback message
  const showFeedbackMessage = useCallback((message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 3000);
  }, []);

  // Validate pattern
  const validatePattern = useCallback((userPattern: PatternItem[]): { isValid: boolean; message: string; accuracy: number } => {
    if (userPattern.length === 0) {
      return { isValid: false, message: 'Commencez par placer un élément !', accuracy: 0 };
    }

    if (userPattern.length < pattern.length) {
      return { isValid: false, message: `Continuez ! Il manque ${pattern.length - userPattern.length} élément(s).`, accuracy: 0 };
    }

    let correctItems = 0;
    for (let i = 0; i < pattern.length; i++) {
      if (userPattern[i] && 
          userPattern[i].emoji === pattern[i].emoji && 
          userPattern[i].color === pattern[i].color) {
        correctItems++;
      }
    }

    const accuracy = (correctItems / pattern.length) * 100;
    const isValid = accuracy === 100;

    let message = '';
    if (isValid) {
      message = `Parfait ! Vous avez reproduit le rythme correctement ! 🎉`;
    } else if (accuracy >= 70) {
      message = `Bien ! ${correctItems}/${pattern.length} éléments corrects. Continuez !`;
    } else {
      message = `Essayez encore ! ${correctItems}/${pattern.length} éléments corrects.`;
    }

    return { isValid, message, accuracy };
  }, [pattern]);

  // Add item to pattern
  const addToPattern = useCallback((item: PatternItem) => {
    if (!allowInteraction || currentPattern.length >= maxLength) return;

    const newPattern = [...currentPattern, item];
    setCurrentPattern(newPattern);
    setTotalAttempts(prev => prev + 1);

    // Auto-validate when pattern is complete
    if (newPattern.length === pattern.length) {
      const validation = validatePattern(newPattern);
      showFeedbackMessage(validation.message, validation.isValid ? 'success' : 'error');
      
      if (validation.isValid) {
        setCompletedPatterns(prev => prev + 1);
        onAction?.('pattern_completed', {
          pattern: newPattern,
          accuracy: validation.accuracy,
          attempts: totalAttempts + 1
        });
      } else {
        onAction?.('pattern_attempt', {
          pattern: newPattern,
          accuracy: validation.accuracy,
          attempts: totalAttempts + 1
        });
      }
    } else {
      onAction?.('item_added', {
        item: item,
        currentLength: newPattern.length,
        totalLength: pattern.length
      });
    }
  }, [currentPattern, allowInteraction, maxLength, pattern.length, validatePattern, showFeedbackMessage, onAction, totalAttempts]);

  // Remove last item
  const removeLastItem = useCallback(() => {
    if (currentPattern.length > 0) {
      const newPattern = currentPattern.slice(0, -1);
      setCurrentPattern(newPattern);
      onAction?.('item_removed', {
        currentLength: newPattern.length,
        totalLength: pattern.length
      });
    }
  }, [currentPattern, onAction, pattern.length]);


  // Reset pattern
  const resetPattern = useCallback(() => {
    setCurrentPattern([]);
    setShowFeedback(false);
    onAction?.('pattern_reset');
  }, [onAction]);

  // Validate current pattern
  const validateCurrentPattern = useCallback(() => {
    const validation = validatePattern(currentPattern);
    showFeedbackMessage(validation.message, validation.isValid ? 'success' : 'error');
    
    onAction?.('pattern_validated', {
      pattern: currentPattern,
      accuracy: validation.accuracy,
      isValid: validation.isValid
    });
  }, [currentPattern, validatePattern, showFeedbackMessage, onAction]);

  // Initialize component
  useEffect(() => {
    setCurrentPattern([]);
    setCompletedPatterns(0);
    setTotalAttempts(0);
  }, [pattern]);

  return (
    <div className="pattern-maker-container p-4 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {resource.name || 'Créateur de Rythmes'}
        </h3>
        <p className="text-sm text-gray-600 mb-3">{resource.question}</p>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          {/* <button
            onClick={playPattern}
            disabled={isPlaying}
            className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-400 rounded-lg transition-colors"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isPlaying ? 'Lecture...' : 'Voir le modèle'}
          </button> */}
          <button
            type="button"
            onClick={validateCurrentPattern}
            className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Valider
          </button>
          <button
            type="button"
            onClick={resetPattern}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Recommencer
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Éléments placés: {currentPattern.length}/{pattern.length}</span>
          <span>Rythmes réussis: {completedPatterns}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentPattern.length / pattern.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Model Pattern (always visible) */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Modèle à reproduire :</h4>
        <div className="flex gap-2">
          {pattern.map((item, index) => (
            <div
              key={`model-${index}`}
              className="w-12 h-12 flex items-center justify-center text-2xl bg-white rounded-lg shadow-sm border-2 border-blue-300"
              style={{ backgroundColor: item.color + '20' }}
            >
              {item.emoji}
            </div>
          ))}
        </div>
        <p className="text-xs text-blue-600 mt-2">
          Reproduisez exactement ce rythme dans l'ordre
        </p>
      </div>

      {/* Current Pattern */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Votre rythme :</h4>
        <div className="min-h-[60px] p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="flex gap-2 flex-wrap">
            {currentPattern.map((item, index) => {
              const isCorrect = index < pattern.length && 
                item.emoji === pattern[index].emoji && 
                item.color === pattern[index].color;
              const isWrong = index < pattern.length && !isCorrect;
              
              return (
                <div
                  key={`current-${index}`}
                  className={`relative w-12 h-12 flex items-center justify-center text-2xl bg-white rounded-lg shadow-sm border-2 ${
                    isCorrect ? 'border-green-500 bg-green-50' :
                    isWrong ? 'border-red-500 bg-red-50' :
                    'border-green-300'
                  }`}
                  style={{ backgroundColor: isCorrect ? item.color + '20' : undefined }}
                >
                  {item.emoji}
                  {isCorrect && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {isWrong && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
            {currentPattern.length < pattern.length && (
              <div className="w-12 h-12 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300 rounded-lg">
                ?
              </div>
            )}
          </div>
        </div>
        {currentPattern.length > 0 && (
          <button
            type="button"
            onClick={removeLastItem}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Supprimer le dernier élément
          </button>
        )}
      </div>

      {/* Available Items */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Éléments disponibles :</h4>
        <div className="flex gap-2 flex-wrap">
          {options.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => addToPattern(item)}
              disabled={!allowInteraction || currentPattern.length >= maxLength}
              className="w-12 h-12 flex items-center justify-center text-2xl bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-300 transition-colors"
              style={{ backgroundColor: item.color + '10' }}
              title={`Cliquez pour ajouter ${item.emoji}`}
            >
              {item.emoji}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Cliquez sur un élément pour l'ajouter à votre rythme
        </p>
      </div>

      {/* Hint */}
      {showHint && currentPattern.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Astuce : Regardez le modèle et reproduisez le même rythme !
            </p>
          </div>
        </div>
      )}

      {/* Feedback */}
      {showFeedback && (
        <div className={`p-3 rounded-lg ${
          feedbackType === 'success' 
            ? 'bg-green-50 text-green-800' 
            : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {feedbackType === 'success' ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            <p className="text-sm">{feedbackMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export { PatternMakerComponent };
export default PatternMakerComponent;